import React from 'react';

import stateData from '~/data/give-happiness-states.json';

export type StateName = keyof typeof stateData;

interface CircleDecoration {
  type: 'circle';
  cx: number;
  cy: number;
  r: number;
  fill: string;
}

interface StarDecoration {
  type: 'star';
  d: string;
  fill: string;
}

type Decoration = CircleDecoration | StarDecoration;

interface StateSpriteProps {
  state: StateName;
  width?: number;
  height?: number;
  className?: string;
  onClick?: () => void;
}

export function StateSprite({ state, width = 200, height = 200, className = '', onClick }: StateSpriteProps) {
  const data = stateData[state];

  if (!data) {
    console.warn(`State data not found for: ${state}`);
    return null;
  }

  return (
    <svg
      width={className?.includes('w-') ? undefined : width}
      height={className?.includes('h-') ? undefined : height}
      viewBox={data.viewBox}
      className={`${className} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      {/* Background circle */}
      <circle
        cx={data.background.circle.cx}
        cy={data.background.circle.cy}
        r={data.background.circle.r}
        fill={data.background.circle.fill}
      />

      {/* Main state shape(s) */}
      {Array.isArray(data.state) ? (
        data.state.map((statePath, index) => <path key={index} d={statePath.d} fill={statePath.fill} />)
      ) : (
        <path d={data.state.d} fill={data.state.fill} />
      )}

      {/* Decorative elements */}
      {(data.decorations as Decoration[]).map((decoration, index) => {
        if (decoration.type === 'circle') {
          return <circle key={index} cx={decoration.cx} cy={decoration.cy} r={decoration.r} fill={decoration.fill} />;
        } else if (decoration.type === 'star') {
          return <path key={index} d={decoration.d} fill={decoration.fill} />;
        }
        return null;
      })}
    </svg>
  );
}

// Component to render all available states
export function StateCollection({
  onStateClick,
  stateSize = 150,
  className = '',
}: {
  onStateClick?: (state: string) => void;
  stateSize?: number;
  className?: string;
}) {
  const states = Object.keys(stateData) as StateName[];

  return (
    <div className={`flex flex-wrap gap-4 ${className}`}>
      {states.map((state) => (
        <div key={state} className='text-center'>
          <StateSprite
            state={state}
            width={stateSize}
            height={stateSize}
            onClick={() => onStateClick?.(state)}
            className='transition-transform hover:scale-105'
          />
          <p className='mt-2 text-sm font-medium'>{state}</p>
        </div>
      ))}
    </div>
  );
}
